import { useReadSessionStreaming } from "@/features/chat/streams/streamSelectors";
import { queryKeys } from "@/features/chat/types";
import {
  addExtractionColumn,
  addGridChatNote,
  approveExtractionSchema,
  createExtractionCheckpoint,
  deleteExtractionColumn,
  deleteExtractionDataset,
  clearExtractionCells,
  deleteExtractionRow,
  deliverChatNotes,
  fastExtractionFillColumns,
  fastExtractionMoreRows,
  finalizeExtractionDataset,
  getExtractionDataset,
  listExtractionDatasets,
  listExtractionRows,
  inspectExtractionGrid,
  listExtractionCheckpoints,
  observeExtractionGrid,
  replaceExtractionSchema,
  restoreExtractionCheckpoint,
  startExtractionCollection,
  stopExtractionCollection,
  updateExtractionColumn,
} from "@/features/data-curation/api/curation";
import type {
  CurationColumnInput,
  CurationDatasetStatus,
  ExtractionDataset,
  ExtractionGridInspectFilters,
} from "@/features/data-curation/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";

const datasetListKey = ["extraction-datasets"] as const;

const datasetKey = (datasetId: string | null) => ["extraction-dataset", datasetId];

const rowsKey = (
  datasetId: string | null,
  page: number,
) => ["extraction-rows", datasetId, page];

const runKey = (datasetId: string | null) => ["extraction-run", datasetId];
const gridKey = (datasetId: string | null) => ["extraction-grid", datasetId];
const checkpointsKey = (datasetId: string | null) => [
  "extraction-checkpoints",
  datasetId,
];
const inspectKey = (
  datasetId: string | null,
  filters: ExtractionGridInspectFilters,
) => ["extraction-grid-inspect", datasetId, filters] as const;

export const useExtractionDatasets = ({
  refetchInterval = 5_000,
}: { refetchInterval?: number } = {}) =>
  useQuery({
    queryKey: datasetListKey,
    queryFn: listExtractionDatasets,
    refetchInterval,
  });

/** A dataset opened in the last half hour opens at once from memory, then refreshes. */
const DATASET_CACHE_MS = 30 * 60_000;

/** How many of the most recently changed datasets the page loads while it is idle. */
const RECENT_PREFETCH_COUNT = 4;

export const useExtractionDataset = (datasetId: string | null) =>
  useQuery({
    queryKey: datasetKey(datasetId),
    queryFn: () => getExtractionDataset(datasetId!),
    enabled: !!datasetId,
    gcTime: DATASET_CACHE_MS,
    refetchInterval: (query) =>
      query.state.data?.status === "finalized" ? false : 2_000,
  });

/** A curation's name for links elsewhere in the app; no polling. */
export const useExtractionDatasetName = (datasetId: string | null) =>
  useQuery({
    queryKey: [...datasetKey(datasetId), "name"],
    queryFn: async () => (await getExtractionDataset(datasetId!)).name,
    enabled: !!datasetId,
    staleTime: 5 * 60_000,
  });

/**
 * Loads a dataset, its first rows, and its grid summary, so that it opens at once.
 * Data younger than `staleTime` is not loaded again.
 */
export const usePrefetchExtractionDataset = () => {
  const queryClient = useQueryClient();
  return useCallback(
    async (dataset: ExtractionDataset, staleTime = 10_000) => {
      const loads = [
        queryClient.prefetchQuery({
          queryKey: datasetKey(dataset.id),
          queryFn: () => getExtractionDataset(dataset.id),
          staleTime,
          gcTime: DATASET_CACHE_MS,
        }),
      ];
      if (dataset.status === "active" || dataset.status === "finalized") {
        loads.push(
          queryClient.prefetchQuery({
            queryKey: rowsKey(dataset.id, 1),
            queryFn: () => listExtractionRows(dataset.id, 1, 200),
            staleTime,
            gcTime: DATASET_CACHE_MS,
          }),
          queryClient.prefetchQuery({
            queryKey: gridKey(dataset.id),
            queryFn: () => observeExtractionGrid(dataset.id),
            staleTime,
            gcTime: DATASET_CACHE_MS,
          }),
        );
      }
      await Promise.all(loads);
    },
    [queryClient],
  );
};

const whenIdle = (callback: () => void) => {
  if (typeof window.requestIdleCallback === "function") {
    const handle = window.requestIdleCallback(callback, { timeout: 2_000 });
    return () => window.cancelIdleCallback(handle);
  }
  const timer = window.setTimeout(callback, 200);
  return () => window.clearTimeout(timer);
};

/**
 * Once `ready`, loads the most recently changed datasets other than the open one, one
 * at a time while the browser is idle, so that a switch to one of them shows it at once.
 */
export const usePrefetchRecentDatasets = (
  datasets: ExtractionDataset[] | undefined,
  openId: string | null,
  ready: boolean,
) => {
  const prefetch = usePrefetchExtractionDataset();
  const recent = useMemo(
    () =>
      (datasets ?? [])
        .filter((dataset) => dataset.id !== openId)
        .slice(0, RECENT_PREFETCH_COUNT),
    [datasets, openId],
  );
  const recentKey = recent.map((dataset) => `${dataset.id}:${dataset.status}`).join("\n");
  const latestRecent = useRef(recent);
  latestRecent.current = recent;
  useEffect(() => {
    if (!ready || !recentKey) return;
    let cancelIdle = () => {};
    let stopped = false;
    const loadNext = (index: number) => {
      const dataset = latestRecent.current[index];
      if (stopped || !dataset) return;
      cancelIdle = whenIdle(() => {
        void prefetch(dataset, 60_000)
          .catch(() => undefined)
          .then(() => loadNext(index + 1));
      });
    };
    loadNext(0);
    return () => {
      stopped = true;
      cancelIdle();
    };
  }, [prefetch, ready, recentKey]);
};

export const useExtractionRows = (
  datasetId: string | null,
  status: CurationDatasetStatus | undefined,
  page: number,
) =>
  useQuery({
    queryKey: rowsKey(datasetId, page),
    queryFn: () => listExtractionRows(datasetId!, page, 200),
    enabled:
      !!datasetId &&
      (status === "active" || status === "finalized"),
    gcTime: DATASET_CACHE_MS,
    refetchInterval: status === "active" ? 2_000 : false,
    // Another page keeps the grid of this one on screen until it loads.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === datasetId ? previous : undefined,
  });

/** Loads a page of rows in the background, so that the pager shows it at once. */
export const usePrefetchExtractionRows = (datasetId: string | null, page: number | null) => {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!datasetId || page == null) return;
    void queryClient.prefetchQuery({
      queryKey: rowsKey(datasetId, page),
      queryFn: () => listExtractionRows(datasetId, page, 200),
      staleTime: 10_000,
    });
  }, [datasetId, page, queryClient]);
};

export const useExtractionGrid = (
  datasetId: string | null,
  status: CurationDatasetStatus | undefined,
) =>
  useQuery({
    queryKey: gridKey(datasetId),
    queryFn: () => observeExtractionGrid(datasetId!),
    enabled:
      !!datasetId &&
      (status === "active" || status === "finalized"),
    gcTime: DATASET_CACHE_MS,
    refetchInterval: status === "active" ? 2_000 : false,
  });

export const useExtractionInspect = (
  datasetId: string | null,
  status: CurationDatasetStatus | undefined,
  filters: ExtractionGridInspectFilters,
  enabled: boolean,
) =>
  useQuery({
    queryKey: inspectKey(datasetId, filters),
    queryFn: () => inspectExtractionGrid(datasetId!, filters),
    enabled:
      enabled &&
      !!datasetId &&
      (status === "active" || status === "finalized"),
    refetchInterval: status === "active" ? 2_000 : false,
    // The grid keeps the rows of the last filters of this dataset until the new ones load.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === datasetId ? previous : undefined,
  });

export const useExtractionCheckpoints = (datasetId: string | null) =>
  useQuery({
    queryKey: checkpointsKey(datasetId),
    queryFn: () => listExtractionCheckpoints(datasetId!),
    enabled: !!datasetId,
  });

/**
 * Show the note that the server put in the dataset's chat after a change on
 * this page. While the agent works, the note waits, and shows when it stops.
 */
export const useShowChatNotes = (datasetId: string | null) => {
  const queryClient = useQueryClient();
  const isSessionStreaming = useReadSessionStreaming();
  return useCallback(() => {
    const chatId = queryClient.getQueryData<ExtractionDataset>(
      datasetKey(datasetId),
    )?.chat_session_id;
    if (!chatId || isSessionStreaming(chatId)) return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.messages(chatId) });
  }, [datasetId, isSessionStreaming, queryClient]);
};

const WAITING_NOTES_RETRY_MS = 5_000;
const MAX_WAITING_NOTES_RETRIES = 12;

/**
 * Move the notes that waited while the dataset's agent worked into its chat:
 * when the page opens the dataset, and when the agent stops. If no page does
 * it, the server moves them before the agent's next turn.
 */
export const useDeliverChatNotes = (
  datasetId: string | null,
  chatId: string | undefined,
  agentWorking: boolean,
) => {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!datasetId || !chatId || agentWorking) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deliver = async (retries: number) => {
      try {
        const { delivered, waiting } = await deliverChatNotes(datasetId);
        if (cancelled) return;
        if (delivered > 0) {
          void queryClient.invalidateQueries({ queryKey: queryKeys.messages(chatId) });
        }
        // The server can hold the chat for a few seconds after this page sees the agent stop.
        if (waiting > 0 && retries < MAX_WAITING_NOTES_RETRIES) {
          timer = setTimeout(() => void deliver(retries + 1), WAITING_NOTES_RETRY_MS);
        }
      } catch {
        // The notes wait for the agent's next turn.
      }
    };
    void deliver(0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [agentWorking, chatId, datasetId, queryClient]);
};

/** Tell the dataset's chat about the cells cleared or the rows deleted in one grid batch. */
export const useAddGridChatNote = (datasetId: string | null) => {
  const showChatNotes = useShowChatNotes(datasetId);
  return useCallback(
    async (batchId: string) => {
      if (!datasetId) return false;
      try {
        const { saved } = await addGridChatNote(datasetId, batchId);
        if (saved) showChatNotes();
        return saved;
      } catch {
        toast.error("The grid changed, but the chat did not get a note about it.");
        return false;
      }
    },
    [datasetId, showChatNotes],
  );
};

export const useCreateExtractionCheckpoint = (datasetId: string | null) => {
  const queryClient = useQueryClient();
  const showChatNotes = useShowChatNotes(datasetId);
  return useMutation({
    mutationFn: ({
      expectedRevision,
      message,
    }: {
      expectedRevision: number;
      message?: string;
    }) =>
      createExtractionCheckpoint(datasetId!, expectedRevision, message),
    onSuccess: () => {
      showChatNotes();
      void queryClient.invalidateQueries({ queryKey: checkpointsKey(datasetId) });
      void queryClient.invalidateQueries({ queryKey: datasetKey(datasetId) });
      void queryClient.invalidateQueries({ queryKey: datasetListKey });
      void queryClient.invalidateQueries({ queryKey: gridKey(datasetId) });
      void queryClient.invalidateQueries({
        queryKey: ["extraction-rows", datasetId],
      });
    },
  });
};

export const useRestoreExtractionCheckpoint = (datasetId: string | null) => {
  const queryClient = useQueryClient();
  const showChatNotes = useShowChatNotes(datasetId);
  return useMutation({
    mutationFn: ({
      checkpointId,
      expectedRevision,
    }: {
      checkpointId: string;
      expectedRevision: number;
    }) =>
      restoreExtractionCheckpoint(
        datasetId!,
        checkpointId,
        expectedRevision,
      ),
    onSuccess: ({ dataset }) => {
      queryClient.setQueryData(datasetKey(datasetId), dataset);
      showChatNotes();
      void queryClient.invalidateQueries({ queryKey: datasetListKey });
      void queryClient.invalidateQueries({ queryKey: checkpointsKey(datasetId) });
      void queryClient.invalidateQueries({ queryKey: gridKey(datasetId) });
      void queryClient.invalidateQueries({
        queryKey: ["extraction-rows", datasetId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["extraction-grid-inspect", datasetId],
      });
    },
  });
};

export const useClearExtractionCells = (datasetId: string | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cells: { rowId: string; columnId: string; revision: number }[]) =>
      clearExtractionCells(datasetId!, cells),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: datasetKey(datasetId) });
      void queryClient.invalidateQueries({ queryKey: gridKey(datasetId) });
      void queryClient.invalidateQueries({
        queryKey: ["extraction-rows", datasetId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["extraction-grid-inspect", datasetId],
      });
    },
  });
};

export const useDeleteExtractionRow = (datasetId: string | null) => {
  const queryClient = useQueryClient();
  const addGridNote = useAddGridChatNote(datasetId);
  return useMutation({
    mutationFn: (rowId: string) => deleteExtractionRow(datasetId!, rowId),
    onSuccess: (batchId) => {
      if (batchId) void addGridNote(batchId);
      void queryClient.invalidateQueries({ queryKey: datasetKey(datasetId) });
      void queryClient.invalidateQueries({ queryKey: datasetListKey });
      void queryClient.invalidateQueries({ queryKey: gridKey(datasetId) });
      void queryClient.invalidateQueries({
        queryKey: ["extraction-rows", datasetId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["extraction-grid-inspect", datasetId],
      });
    },
  });
};

export const useDeleteExtractionDataset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (datasetId: string) => deleteExtractionDataset(datasetId),
    onSuccess: (_result, datasetId) => {
      queryClient.removeQueries({ queryKey: datasetKey(datasetId) });
      queryClient.removeQueries({ queryKey: ["extraction-rows", datasetId] });
      queryClient.removeQueries({ queryKey: runKey(datasetId) });
      queryClient.removeQueries({ queryKey: gridKey(datasetId) });
      queryClient.removeQueries({
        queryKey: ["extraction-grid-inspect", datasetId],
      });
      void queryClient.invalidateQueries({ queryKey: datasetListKey });
    },
  });
};

/** Put a schema response in the cache; a poll that started earlier must not overwrite it. */
const useStoreDataset = (datasetId: string | null) => {
  const queryClient = useQueryClient();
  return useCallback(
    async (dataset: ExtractionDataset) => {
      await queryClient.cancelQueries({ queryKey: datasetKey(datasetId), exact: true });
      queryClient.setQueryData(datasetKey(datasetId), dataset);
      void queryClient.invalidateQueries({ queryKey: datasetListKey });
    },
    [datasetId, queryClient],
  );
};

export const useReplaceExtractionSchema = (datasetId: string | null) => {
  const storeDataset = useStoreDataset(datasetId);
  const showChatNotes = useShowChatNotes(datasetId);
  return useMutation({
    mutationFn: ({
      expectedRevision,
      columns,
      chatNote = true,
    }: {
      expectedRevision: number;
      columns: CurationColumnInput[];
      /** False when a message to the agent already lists the changes. */
      chatNote?: boolean;
    }) => replaceExtractionSchema(datasetId!, expectedRevision, columns, { chatNote }),
    onSuccess: async (dataset, { chatNote = true }) => {
      await storeDataset(dataset);
      if (chatNote) showChatNotes();
    },
  });
};

export const useAddExtractionColumn = (datasetId: string | null) => {
  const queryClient = useQueryClient();
  const storeDataset = useStoreDataset(datasetId);
  const showChatNotes = useShowChatNotes(datasetId);
  return useMutation({
    mutationFn: ({
      expectedRevision,
      column,
      chatNote = true,
    }: {
      expectedRevision: number;
      column: CurationColumnInput;
      /** False when a message to the agent already tells of the column. */
      chatNote?: boolean;
    }) => addExtractionColumn(datasetId!, expectedRevision, column, { chatNote }),
    onSuccess: async (dataset, { chatNote = true }) => {
      await storeDataset(dataset);
      if (chatNote) showChatNotes();
      void queryClient.invalidateQueries({ queryKey: gridKey(datasetId) });
    },
  });
};

export const useUpdateExtractionColumn = (datasetId: string | null) => {
  const queryClient = useQueryClient();
  const storeDataset = useStoreDataset(datasetId);
  const showChatNotes = useShowChatNotes(datasetId);
  return useMutation({
    mutationFn: ({
      columnId,
      expectedRevision,
      column,
    }: {
      columnId: string;
      expectedRevision: number;
      column: Omit<CurationColumnInput, "column_id">;
    }) => updateExtractionColumn(datasetId!, columnId, expectedRevision, column),
    onSuccess: async ({ dataset }) => {
      await storeDataset(dataset);
      showChatNotes();
      void queryClient.invalidateQueries({ queryKey: gridKey(datasetId) });
      void queryClient.invalidateQueries({
        queryKey: ["extraction-rows", datasetId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["extraction-grid-inspect", datasetId],
      });
    },
  });
};

export const useDeleteExtractionColumn = (datasetId: string | null) => {
  const queryClient = useQueryClient();
  const storeDataset = useStoreDataset(datasetId);
  const showChatNotes = useShowChatNotes(datasetId);
  return useMutation({
    mutationFn: ({
      columnId,
      expectedRevision,
    }: {
      columnId: string;
      expectedRevision: number;
    }) => deleteExtractionColumn(datasetId!, columnId, expectedRevision),
    onSuccess: async (dataset) => {
      await storeDataset(dataset);
      showChatNotes();
      void queryClient.invalidateQueries({ queryKey: gridKey(datasetId) });
      void queryClient.invalidateQueries({
        queryKey: ["extraction-rows", datasetId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["extraction-grid-inspect", datasetId],
      });
    },
  });
};

export const useApproveExtractionSchema = (datasetId: string | null) => {
  const queryClient = useQueryClient();
  const storeDataset = useStoreDataset(datasetId);
  const showChatNotes = useShowChatNotes(datasetId);
  return useMutation({
    mutationFn: ({
      expectedRevision,
      startCollection = true,
    }: {
      expectedRevision: number;
      /** False: the agent waits until the user starts the collection. */
      startCollection?: boolean;
    }) => approveExtractionSchema(datasetId!, expectedRevision, { startCollection }),
    onSuccess: async (dataset, { startCollection = true }) => {
      await storeDataset(dataset);
      if (!startCollection) showChatNotes();
      void queryClient.invalidateQueries({ queryKey: gridKey(datasetId) });
      void queryClient.invalidateQueries({
        queryKey: ["extraction-rows", datasetId],
      });
    },
  });
};

export const useStartExtractionCollection = (datasetId: string | null) => {
  const storeDataset = useStoreDataset(datasetId);
  return useMutation({
    mutationFn: () => startExtractionCollection(datasetId!),
    onSuccess: (dataset) => storeDataset(dataset),
  });
};

export const useStopExtractionCollection = (datasetId: string | null) => {
  const storeDataset = useStoreDataset(datasetId);
  return useMutation({
    mutationFn: () => stopExtractionCollection(datasetId!),
    onSuccess: (dataset) => storeDataset(dataset),
  });
};

export const useFinalizeExtractionDataset = (datasetId: string | null) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (expectedRevision: number) =>
      finalizeExtractionDataset(datasetId!, expectedRevision),
    onSuccess: (dataset) => {
      queryClient.setQueryData(datasetKey(datasetId), dataset);
      void queryClient.invalidateQueries({ queryKey: datasetListKey });
      void queryClient.invalidateQueries({
        queryKey: ["extraction-rows", datasetId],
      });
    },
  });
};

/** Refetch one curation and the list, e.g. after its chat was created. */
export const useRefreshExtractionDataset = () => {
  const queryClient = useQueryClient();
  return useCallback(
    (datasetId: string) => {
      void queryClient.invalidateQueries({ queryKey: datasetKey(datasetId) });
      void queryClient.invalidateQueries({ queryKey: datasetListKey });
    },
    [queryClient],
  );
};

/** Runs a job of the Fast dataset builder; the dataset polls its progress meanwhile. */
const useFastJob = <T,>(datasetId: string | null, run: (datasetId: string, input: T) => Promise<ExtractionDataset>) => {
  const queryClient = useQueryClient();
  const storeDataset = useStoreDataset(datasetId);
  return useMutation({
    mutationFn: (input: T) => run(datasetId!, input),
    onMutate: () => {
      // The job starts at once: the progress card shows it before the next poll.
      void queryClient.invalidateQueries({ queryKey: datasetKey(datasetId), exact: true });
    },
    onSuccess: async (dataset) => {
      await storeDataset(dataset);
      void queryClient.invalidateQueries({ queryKey: gridKey(datasetId) });
      void queryClient.invalidateQueries({ queryKey: ["extraction-rows", datasetId] });
    },
    onError: (error) => {
      void queryClient.invalidateQueries({ queryKey: datasetKey(datasetId), exact: true });
      toast.error(error instanceof Error ? error.message : "The dataset builder failed.");
    },
  });
};

/** More rows: the Fast builder reads more papers of its search. */
export const useFastMoreRows = (datasetId: string | null) =>
  useFastJob<void>(datasetId, (id) => fastExtractionMoreRows(id));

/** Fills added or edited columns of a Fast dataset from the passages its build read. */
export const useFastFillColumns = (datasetId: string | null) =>
  useFastJob<string[]>(datasetId, (id, columnIds) => fastExtractionFillColumns(id, columnIds));
